import type { ProductCard, ProductDetails } from "@/lib/centra";

/** The only fields needed to derive a reviews.io identifier. Kept minimal so
 * both full products and the (narrower) variant entries in `product.variants`
 * satisfy it. */
type ReviewsIdentifierInput = Pick<ProductCard, "sku" | "productSku">;

export const generateReviewsIdentifier = (
	product: ReviewsIdentifierInput | string,
): string[] => {
	if (typeof product === "string") return [product];

	if (product.productSku?.endsWith("-")) {
		const suffix = product.sku.replace(product.productSku, "");
		return [`1/no-${product.productSku}-${suffix}`];
	}

	return [`1/no-${product.sku}`, `1/no-${product.sku}-`];
};

/**
 * Build the reviews.io identifiers for a product AND all of its colour
 * variants. reviews.io aggregates reviews across every id passed in the
 * (semicolon-separated) `data-sku`, so listing all sibling variants makes each
 * colour's PDP show the combined reviews for the whole product.
 */
export const generateVariantReviewsIdentifiers = (
	product: ProductCard | ProductDetails,
): string[] => {
	const products = [product, ...(product.variants ?? [])];
	const ids = products.flatMap((variant) => generateReviewsIdentifier(variant));
	return Array.from(new Set(ids));
};
